import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, ApiError } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import {
  Alert,
  Card,
  DetailList,
  EmptyState,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../../components/ui/display';
import { DataTable } from '../../tables/DataTable';
import type { RevenueAccount, RevenueSummary } from '../../types/models';
import { formatMoney, formatPercent, monthName } from '../../utils/format';
import { Stat } from '../marketing/MarketingPage';
import { AccountAppsCard } from '../assignments/AccountAppsCard';
import { BankAccountCard } from './BankAccountCard';
import { resourceKey } from './MasterPage';

interface AccountRevenue {
  days: { date: string; revenueAmount: string; revenueInr: string }[];
  months: {
    month: string;
    revenueAmount: string;
    revenueInr: string;
    /** Whole-month estimate the deduction is measured against (settled months only). */
    estimatedAmount: string | null;
    estimatedInr: string | null;
    /** INR per unit of the account currency. */
    estimatedRate: string | null;
    actualRate: string | null;
    /** From the month-end payout; null until it is entered. */
    actualAmount: string | null;
    actualInr: string | null;
    deduction: string | null;
    deductionPercent: string | null;
    /** Unpaid months below the payment threshold included in this payout. */
    carriedMonths: string[];
  }[];
  totals: RevenueSummary;
}

const compactInr = (v: number) =>
  v >= 1e7
    ? `₹${(v / 1e7).toFixed(1)}Cr`
    : v >= 1e5
      ? `₹${(v / 1e5).toFixed(1)}L`
      : v >= 1e3
        ? `₹${(v / 1e3).toFixed(0)}k`
        : `₹${v}`;

/** Small "@ ₹84.50 / $1" line under an INR amount (non-INR accounts only). */
function RateNote({ rate, cur }: { rate: string | null; cur: string }) {
  if (!rate || cur === 'INR') return null;
  return (
    <div className="num mt-0.5 text-[11px] leading-tight font-normal text-slate-400">
      @ {formatMoney(rate)} / {formatMoney('1', cur).replace(/\.00$/, '')}
    </div>
  );
}

export function RevenueAccountDetailPage() {
  const { id = '' } = useParams();
  const [range, setRange] = useDateFilter('thisMonth');
  const account = useQuery({
    queryKey: [...resourceKey('revenue-accounts'), 'detail', id],
    queryFn: () => api.get<RevenueAccount>(`/revenue-accounts/${id}`),
  });
  const revenue = useQuery({
    queryKey: ['revenue', 'account', id, range.startDate, range.endDate],
    queryFn: () =>
      api.get<AccountRevenue>(`/revenue-accounts/${id}/revenue`, {
        startDate: range.startDate,
        endDate: range.endDate,
      }),
    placeholderData: keepPreviousData,
  });

  if (account.isError)
    return (
      <Alert tone="error">
        {account.error instanceof ApiError ? account.error.message : 'Failed to load'}
      </Alert>
    );
  const r = account.data;
  if (!r) return <Spinner />;
  const cur = r.currencyCode;
  const totals = revenue.data?.totals;
  const original = totals?.byCurrency.find((c) => c.currencyCode === cur);
  const chart = (revenue.data?.days ?? []).map((d) => ({
    date: d.date.slice(5),
    inr: Number(d.revenueInr),
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={
          <Link
            to={
              { ADMOB: '/admob-accounts', ADX: '/adx-accounts' }[r.revenueSource?.code ?? ''] ??
              '/revenue-accounts'
            }
            className="text-brand-700 hover:underline"
          >
            {{ ADMOB: 'AdMob accounts', ADX: 'AdX accounts' }[r.revenueSource?.code ?? ''] ??
              'Revenue accounts'}
          </Link>
        }
        title={r.accountName}
        description={[r.revenueSource?.name, r.accountIdentifier, cur].filter(Boolean).join(' · ')}
        actions={<StatusBadge status={r.status} />}
      />
      <Card title="Account information">
        <DetailList
          items={[
            {
              label: 'Used by',
              value: r.assignments?.length ? (
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {r.assignments.map((x) => (
                    <Link
                      key={x.id}
                      to={`/apps/${x.app.id}`}
                      className="inline-flex items-center gap-1.5 text-brand-700 hover:underline"
                    >
                      <AppIcon src={x.app.iconUrl} name={x.app.name} size={20} />
                      {x.app.name}
                    </Link>
                  ))}
                </span>
              ) : (
                'Not assigned to an app'
              ),
            },
            { label: 'Revenue source', value: r.revenueSource?.name },
            { label: 'Account ID', value: r.accountIdentifier },
            { label: 'Login email', value: r.loginEmail },
            { label: 'Access email', value: r.accessEmail },
            { label: 'Currency', value: cur },
            {
              label: 'Payment threshold',
              value: r.payoutThreshold
                ? `${formatMoney(r.payoutThreshold, cur)} (smaller months carry forward)`
                : 'Pays every month',
            },
            {
              label: 'GST account',
              value: r.gstAccount ? `${r.gstAccount.legalName} (${r.gstAccount.gstin})` : 'None',
            },
          ]}
        />
      </Card>

      <AccountAppsCard resource="revenue-accounts" accountId={r.id} />
      <BankAccountCard accountId={r.id} accountName={r.accountName} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-900">Revenue</h2>
        <DateRangeFilter value={range} onChange={setRange} />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        {cur !== 'INR' && (
          <Stat
            label={`Revenue (${cur})`}
            value={formatMoney(original?.revenueAmount ?? '0', cur)}
          />
        )}
        <Stat label="Revenue (INR)" value={formatMoney(totals?.revenueInr)} tone="profit" />
        <Stat label="Days entered" value={totals ? String(totals.count) : '—'} />
      </div>

      <Card title="Daily revenue (INR)">
        {chart.length === 0 ? (
          <EmptyState title="No revenue in this period" />
        ) : (
          <div className="h-64" role="img" aria-label="Daily revenue chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={compactInr}
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                />
                <Tooltip formatter={(v) => formatMoney(String(v ?? 0))} />
                <Bar dataKey="inr" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card
        title="Monthly revenue"
        description="Estimated = daily entries; Actual = payout entered in Month-End Actuals. Deduction = actual − estimated, and % of the estimate held back."
        bodyClassName="p-0"
      >
        <DataTable
          tableId="revenue-account"
          rows={revenue.data?.months}
          rowKey={(m) => m.month}
          loading={revenue.isLoading}
          columns={[
            {
              key: 'month',
              header: 'Month',
              render: (m) => `${monthName(Number(m.month.slice(5)), true)} ${m.month.slice(0, 4)}`,
            },
            {
              key: 'estimated',
              header: `Estimated (${cur})`,
              align: 'right',
              render: (m) => (
                <>
                  {formatMoney(m.estimatedAmount ?? m.revenueAmount, cur)}
                  {m.carriedMonths.length > 0 && (
                    <span className="block text-[11px] text-slate-400">
                      incl. carried{' '}
                      {m.carriedMonths.map((k) => monthName(Number(k.slice(5)))).join(', ')}
                    </span>
                  )}
                </>
              ),
            },
            {
              key: 'actual',
              header: `Actual (${cur})`,
              align: 'right',
              render: (m) =>
                m.actualAmount === null ? (
                  <Link
                    to={`/month-end?month=${m.month}`}
                    className="text-xs text-slate-400 hover:text-brand-700 hover:underline"
                  >
                    Not entered
                  </Link>
                ) : (
                  <span className="font-medium">{formatMoney(m.actualAmount, cur)}</span>
                ),
            },
            {
              key: 'deduction',
              header: `Deduction (${cur})`,
              align: 'right',
              render: (m) =>
                m.deduction === null ? (
                  <span className="text-slate-300">—</span>
                ) : (
                  <span className={Number(m.deduction) < 0 ? 'text-rose-600' : 'text-slate-700'}>
                    {formatMoney(m.deduction, cur)}
                  </span>
                ),
            },
            {
              key: 'deductionPercent',
              header: 'Deduction %',
              align: 'right',
              render: (m) =>
                m.deductionPercent === null ? (
                  <span className="text-slate-300">—</span>
                ) : (
                  <span
                    className={Number(m.deductionPercent) > 0 ? 'text-rose-600' : 'text-slate-700'}
                  >
                    {formatPercent(m.deductionPercent)}
                  </span>
                ),
            },
            {
              key: 'inr',
              header: 'Estimated (INR)',
              align: 'right',
              render: (m) => (
                <>
                  {formatMoney(m.estimatedInr ?? m.revenueInr)}
                  <RateNote rate={m.estimatedRate} cur={cur} />
                </>
              ),
            },
            {
              key: 'received',
              header: 'Received (INR)',
              align: 'right',
              render: (m) =>
                m.actualInr === null ? (
                  <span className="text-slate-300">—</span>
                ) : (
                  <>
                    <span className="font-medium">{formatMoney(m.actualInr)}</span>
                    <RateNote rate={m.actualRate} cur={cur} />
                  </>
                ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
